import type { QueryOptions } from '@tanstack/react-query';
import { queryOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';

import type { ProfessionalByIdOrRouteResponse } from './types';

/**
 * Fetches professionals for a specific shop
 * @param params - The shop ID, include options, and language
 * @returns Promise<ProfessionalsResponse> - Array of professionals
 */
const fetchProfessionalByIdOrRoute = async (
  shopId: string,
  barberIdOrRoute: string | undefined,
  include: string[] = ['photos', 'days_off', 'days_on', 'schedules'],
): Promise<ProfessionalByIdOrRouteResponse> => {
  if (!shopId) {
    throw new Error('Shop ID is required to fetch professionals');
  }

  if (!barberIdOrRoute) {
    throw new Error('Barber ID or route is required to fetch professional');
  }

  if (barberIdOrRoute === 'any') {
    return {
      nextAvailableTime: new Date(),
      nextAvailableTimeText: 'Today at 9:00 AM',
      barber: {
        id: 'any',
        firstName: 'Any',
        lastName: '',
        route: null,
        order: 0,
        photos: [],
        allowMultipleServices: undefined,
      },
    };
  }

  const params: Record<string, string> = {};
  if (include.length > 0) {
    params.include = include.join(',');
  }

  const { data } = await apiClient.get<ProfessionalByIdOrRouteResponse>(
    `/v1/shop/${shopId}/professional/${barberIdOrRoute}`,
    { params },
  );

  return data;
};

export const getBarberByIdOrRouteQuery = (
  shopId: string,
  barberIdOrRoute: string | undefined,
  include: string[] = ['photos', 'days_off', 'days_on', 'schedules'],
  options?: Omit<
    QueryOptions<ProfessionalByIdOrRouteResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryKey = [
    'booking',
    'professionals',
    shopId,
    barberIdOrRoute,
    { include },
  ];

  return queryOptions({
    queryKey,
    queryFn: () =>
      fetchProfessionalByIdOrRoute(shopId, barberIdOrRoute, include),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    ...options,
  });
};
